import React, { useEffect } from "react"
import { CommonButton } from "@/components/common/CommonButton"
import { useConnect } from "wagmi"
import { useTranslation } from "react-i18next"
import { Box, useTheme } from "@mui/material"
import AccountBalanceWalletOutlinedIcon from "@mui/icons-material/AccountBalanceWalletOutlined"
import { useDeviceDetect } from "@/core/hook/useDeviceDetect"
import AlertContainer from "@/core/store/AlertContainer"
import { useApolloClient } from "@apollo/client"
import { handleLoginConnector } from "@/core/utils/handleLoginConnector"
import { getCookie } from "@/core/utils/cookieUtils"
import { AuthState } from "@/core/store/AuthContext"

export type DiverWalletConnectorProps = {
  handleClose: () => void
  updateAuthState?: (newAuthState: Partial<AuthState>) => void
}

export const DiverWalletConnector: React.FunctionComponent<
  DiverWalletConnectorProps
> = ({ handleClose, updateAuthState }) => {
  const { t } = useTranslation()
  const client = useApolloClient()
  const { pushAlert } = AlertContainer.useContainer()
  const { connect, connectors } = useConnect({
    onError: (err: any) => {
      if (err.message.includes("Invalid address")) {
        pushAlert({
          message:
            "Current wallet not support BitCoin,switch to DIVER Time Network",
        })
      } else {
        pushAlert({
          message: err.message || "error",
        })
      }
    },
    onSuccess: () => {
      (window.diverWallet || window.ethereum)
      .request({
        method: "eth_requestAccounts",
      })
      .then((res : string[])=> {
        const [address] = res
        if (address && !getCookie("jwt-token") && updateAuthState) {
          handleLoginConnector(client, address, pushAlert).then(
            ({ sign, nonce }) => {
              updateAuthState({ sign, nonce })
            }
          )
        }
      })
    }
  })

  const { breakpoints } = useTheme()
  const { isIOS, isAndroid, isDiverApp, isMobile } = useDeviceDetect()

  const connectWallet = () => {
    const connector = isDiverApp
      ? connectors[0]
      : connectors.find(item => item.name === "DiverWallet")
    connector && connect({ connector })
    handleClose()
  }

  const goToInstallDiverWallet = () => {
    if (isIOS) {
      location.href = import.meta.env.VITE_DIVER_WALLET_IOS
    } else if (isAndroid) {
      location.href = import.meta.env.VITE_DIVER_WALLET_ANDROID
    } else {
      window.open(import.meta.env.VITE_DIVER_WALLET_URL, "_blank")
    }
  }

  const isInstalledWallet = () => {
    const provider = window.diverWallet || window.ethereum
    return provider?.isDiverWallet || isMobile
  }

  const directByDeepLink = () => {
    const a = document.createElement("a")
    a.setAttribute(
      "href",
      `${import.meta.env.VITE_DEEP_LINK_URL}${location.pathname}`
    )
    a.click()
  }

  const goToDiverWalletApp = () => {
    const userAgent = navigator.userAgent
    if (userAgent.includes("Line")) {
      directByDeepLink()
    } else {
      fetch(
        "https://firebasedynamiclinks.googleapis.com/v1/shortLinks?key=AIzaSyBdkG-IOnwHm5ORjyaod8bjy4n7zgin660",
        {
          method: "POST",
          body: JSON.stringify({
            dynamicLinkInfo: {
              domainUriPrefix: "https://diverwallet.page.link",
              link: `${import.meta.env.VITE_DEEP_LINK_URL}${location.pathname}`,
              androidInfo: {
                androidPackageName: "com.hyerdesign.diver.wallet",
              },
              iosInfo: {
                iosBundleId: "com.bigant.diver.limited.wallet",
                iosAppStoreId: "6444421722",
              },
            },
          }),
        }
      )
        .then(res => res.json())
        .then(res => {
          const a = document.createElement("a")
          a.setAttribute("href", res.shortLink)
          a.click()
        })
        .catch(() => {
          directByDeepLink()
        })
    }
  }

  useEffect(() => {
    if (!isDiverApp) {
      if (isMobile) {
        goToDiverWalletApp()
      }
      return
    }
  }, [])

  return (
    <Box
      className="px-16 py-20 sm:p-[60px] bg-white rounded-[20px] flex flex-col justify-center items-center"
      sx={{
        width: "95%",
        margin: "auto",
        [breakpoints.up("sm")]: { width: 540 },
      }}
    >
      <img
        className="w-5/12 sm:w-1/3 object-contain mb-7 sm:mb-9"
        src={
          isInstalledWallet()
            ? "/images/diverWallet.svg"
            : "/images/diverWallet_desaturated.svg"
        }
        alt="diver_wallet"
      />
      <div className="text-center mb-8 w-full">
        <CommonButton
          variant="contained"
          sx={{
            width: "100%",
            height: 56,
            fontSize: 14,
            lineHeight: 1,
            fontWeight: 700,
            paddingX: 12,
            alignItems: "center",
            backgroundColor: "#18539E",
            "&: hover": {
              backgroundColor: "#376AB3",
            },
            [breakpoints.up("sm")]: {
              fontSize: 18,
              height: 70,
              paddingX: 0,
              boxShadow: "0px 5px 5px 0px rgba(0, 26, 59, 0.2)",
            },
          }}
          onClick={connectWallet}
        >
          <AccountBalanceWalletOutlinedIcon
            sx={{
              fontSize: 20,
              marginRight: 1.5,
              [breakpoints.up("sm")]: {
                fontSize: 28,
                marginRight: "10px",
              },
            }}
          />
          <span className="inline-block whitespace-nowrap">
            {t("wallet_connecting")}
          </span>
        </CommonButton>
        <div className="text-sm mt-1.5 font-medium text-[#666666]">
          {t("wallet_connecting_text")}
        </div>
      </div>
      <div className="text-center w-full">
        <CommonButton
          className="w-full"
          variant="outlined"
          sx={{
            width: "100%",
            height: 56,
            fontSize: 14,
            lineHeight: 1,
            fontWeight: 700,
            alignItems: "center",
            paddingX: 12,
            backgroundColor: "#fff",
            [breakpoints.up("sm")]: {
              fontSize: 18,
              height: 70,
              paddingX: 0,
              boxShadow: "0px 5px 5px 0px rgba(0, 26, 59, 0.2)",
            },
          }}
          onClick={goToInstallDiverWallet}
        >
          <img
            src="/images/ic.svg"
            alt="ic"
            className="mr-[10px] w-[14px] sm:w-5"
          />
          <span className="inline-block whitespace-nowrap">
            {isMobile
              ? t("wallet_connecting_download_rwd")
              : t("wallet_connecting_download_web")}
          </span>
        </CommonButton>
        <div className="text-sm mt-1.5 font-medium text-[#666666]">
          {t("wallet_connecting_download_text")}
        </div>
      </div>
    </Box>
  )
}
